// src/components/games/crossroads/GameLobby.tsx
// Multiplayer game lobby for Crossroads TCG

import React, { useState, useEffect } from 'react';
import {
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonButton,
  IonList,
  IonItem,
  IonLabel,
  IonBadge,
  IonIcon,
  IonText,
  IonSpinner,
  IonRefresher,
  IonRefresherContent,
  useIonToast,
} from '@ionic/react';
import {
  playOutline,
  timeOutline,
  personAddOutline,
  refreshOutline,
  trophyOutline,
} from 'ionicons/icons';
import { ActiveMultiplayerGame, getUserActiveMultiplayerGames } from '../../../services/crossroadsMultiplayerManager';
import './GameLobby.css';

interface GameLobbyProps {
  onJoinGame: (gameId: string, playerIndex: number) => void;
  onChallengePlayer: () => void;
}

export const GameLobby: React.FC<GameLobbyProps> = ({ onJoinGame, onChallengePlayer }) => {
  const [activeGames, setActiveGames] = useState<ActiveMultiplayerGame[]>([]);
  const [loading, setLoading] = useState(true);
  const [presentToast] = useIonToast();

  const loadActiveGames = async () => {
    setLoading(true);
    const result = await getUserActiveMultiplayerGames();

    if (result.success && result.games) {
      setActiveGames(result.games);
    } else {
      presentToast({
        message: result.error || 'Failed to load games',
        duration: 2000,
        color: 'danger',
      });
    }

    setLoading(false);
  };

  useEffect(() => {
    loadActiveGames();

    // Auto-refresh every 30 seconds
    const interval = setInterval(loadActiveGames, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = async (event: CustomEvent) => {
    await loadActiveGames();
    event.detail.complete();
  };

  const formatTimeAgo = (dateString: string): string => {
    const now = new Date().getTime();
    const then = new Date(dateString).getTime();
    const diffSeconds = Math.floor((now - then) / 1000);

    if (diffSeconds < 60) return `${diffSeconds}s ago`;
    if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m ago`;
    if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)}h ago`;
    return `${Math.floor(diffSeconds / 86400)}d ago`;
  };

  return (
    <div className="game-lobby">
      <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
        <IonRefresherContent />
      </IonRefresher>

      <IonCard>
        <IonCardHeader>
          <IonCardTitle>
            <IonIcon icon={trophyOutline} /> Multiplayer Lobby
          </IonCardTitle>
        </IonCardHeader>
        <IonCardContent>
          <IonButton
            expand="block"
            onClick={onChallengePlayer}
            color="primary"
          >
            <IonIcon icon={personAddOutline} slot="start" />
            Challenge a Friend
          </IonButton>

          <div style={{ marginTop: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <IonText>
                <h3>Active Games ({activeGames.length})</h3>
              </IonText>
              <IonButton
                fill="clear"
                size="small"
                onClick={loadActiveGames}
                disabled={loading}
              >
                <IonIcon icon={refreshOutline} />
              </IonButton>
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <IonSpinner />
                <IonText color="medium">
                  <p>Loading games...</p>
                </IonText>
              </div>
            ) : activeGames.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <IonText color="medium">
                  <p>No active games</p>
                  <p style={{ fontSize: '0.9em' }}>Challenge a friend to start playing!</p>
                </IonText>
              </div>
            ) : (
              <IonList>
                {activeGames.map((game) => (
                  <IonItem
                    key={game.gameId}
                    button
                    onClick={() => onJoinGame(game.gameId, game.playerIndex)}
                    className="game-lobby-item"
                  >
                    <IonLabel>
                      <h2>vs {game.opponentName}</h2>
                      <p>
                        <IonIcon icon={timeOutline} style={{ fontSize: '0.9em', marginRight: '4px' }} />
                        {formatTimeAgo(game.lastActionAt)}
                      </p>
                    </IonLabel>
                    {game.isMyTurn ? (
                      <IonBadge color="success" slot="end">
                        Your Turn
                      </IonBadge>
                    ) : (
                      <IonBadge color="medium" slot="end">
                        Waiting
                      </IonBadge>
                    )}
                    <IonIcon icon={playOutline} slot="end" color="primary" />
                  </IonItem>
                ))}
              </IonList>
            )}
          </div>
        </IonCardContent>
      </IonCard>

      <IonCard>
        <IonCardContent>
          <IonText color="medium">
            <h4>How Multiplayer Works</h4>
            <ul style={{ fontSize: '0.9em', lineHeight: '1.6' }}>
              <li>Challenge friends via the Challenge button</li>
              <li>Accept invitations from your chat messages</li>
              <li>Take turns playing - you'll be notified when it's your turn</li>
              <li>Games auto-save - you can leave and come back anytime</li>
              <li>Inactive games are abandoned after 15 minutes of no activity</li>
            </ul>
          </IonText>
        </IonCardContent>
      </IonCard>
    </div>
  );
};

export default GameLobby;
